// Copyright (C) 2026 Corvus Robotics
//
// SPDX-License-Identifier: MIT

import './styles.scss';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Tag from 'antd/lib/tag';
import Checkbox from 'antd/lib/checkbox';
import { Label, SerializedLabeledFor } from 'cvat-core-wrapper';

interface Props {
    labels: Label[];
    labeledFor: SerializedLabeledFor[];
    onChange?: (labelId: number, value: boolean) => void;
}

function LabeledForCheckboxes(props: Props): JSX.Element {
    const { labels, labeledFor, onChange } = props;

    // Local set of checked label IDs, derived from props but mutable so the
    // checkbox flips instantly without waiting on a refetch.
    const initialChecked = useMemo(
        () => new Set<number>(
            labeledFor.filter((lf) => lf.labeled !== false).map((lf) => lf.label),
        ),
        [labeledFor],
    );
    const [checked, setChecked] = useState<Set<number>>(initialChecked);

    useEffect(() => {
        setChecked(initialChecked);
    }, [initialChecked]);

    const handleChange = useCallback((labelId: number, value: boolean) => {
        setChecked((prev) => {
            const next = new Set(prev);
            if (value) {
                next.add(labelId);
            } else {
                next.delete(labelId);
            }
            return next;
        });
        if (onChange) {
            onChange(labelId, value);
        }
    }, [onChange]);

    const topLevelLabels = labels.filter((l: any) => !l.hasParent);

    if (topLevelLabels.length === 0) {
        return <div className='cvat-labeled-for-checkboxes' />;
    }

    return (
        <div className='cvat-labeled-for-checkboxes'>
            {topLevelLabels.map((label: any) => (
                <div key={label.id} className='cvat-labeled-for-item'>
                    <Checkbox
                        checked={checked.has(label.id)}
                        onChange={(e) => handleChange(label.id, e.target.checked)}
                    />
                    <Tag color={label.color || '#666'}>
                        {label.name || `Label ${label.id}`}
                    </Tag>
                </div>
            ))}
        </div>
    );
}

export default LabeledForCheckboxes;
